// Kept in sync with Synctera's EddCustomerEmploymentTypeEnum enum on backend
export enum EmploymentType {
    CONTRACTOR = 'CONTRACTOR',
    FULL_TIME = 'FULL_TIME',
    INTERN = 'INTERN',
    OTHER = 'OTHER',
    PART_TIME = 'PART_TIME',
    SELF_EMPLOYED = 'SELF_EMPLOYED',
    STUDENT = 'STUDENT',
    RETIRED = 'RETIRED',
    TEMPORARY = 'TEMPORARY',
    VOLUNTEER = 'VOLUNTEER',
}

export const READABLE_EMPLOYMENT_TYPE_MAP: Record<
    string,
    Partial<EmploymentType>
> = {
    Employed: EmploymentType.FULL_TIME,
    'Self-Employed': EmploymentType.SELF_EMPLOYED,
    Retired: EmploymentType.RETIRED,
    Student: EmploymentType.STUDENT,
    Unemployed: EmploymentType.OTHER,
};

// Kept in sync with Synctera's IndustryType enum on backend
export enum IndustryType {
    ACCOMMODATION_AND_FOOD_SERVICES = 'ACCOMMODATION_AND_FOOD_SERVICES',
    ADMINISTRATIVE_AND_SUPPORT_AND_WASTE_MANAGEMENT_AND_REMEDIATION_SERVICES = 'ADMINISTRATIVE_AND_SUPPORT_AND_WASTE_MANAGEMENT_AND_REMEDIATION_SERVICES',
    AGRICULTURE_FORESTRY_FISHING_AND_HUNTING = 'AGRICULTURE_FORESTRY_FISHING_AND_HUNTING',
    ARTS_ENTERTAINMENT_AND_RECREATION = 'ARTS_ENTERTAINMENT_AND_RECREATION',
    CONSTRUCTION = 'CONSTRUCTION',
    EDUCATIONAL_SERVICES = 'EDUCATIONAL_SERVICES',
    FINANCE_AND_INSURANCE = 'FINANCE_AND_INSURANCE',
    HEALTH_CARE_AND_SOCIAL_ASSISTANCE = 'HEALTH_CARE_AND_SOCIAL_ASSISTANCE',
    INFORMATION = 'INFORMATION',
    MANAGEMENT_OF_COMPANIES_AND_ENTERPRISES = 'MANAGEMENT_OF_COMPANIES_AND_ENTERPRISES',
    MANUFACTURING = 'MANUFACTURING',
    MINING_QUARRYING_AND_OIL_AND_GAS_EXTRACTION = 'MINING_QUARRYING_AND_OIL_AND_GAS_EXTRACTION',
    OTHER_SERVICES = 'OTHER_SERVICES',
    PROFESSIONAL_SCIENTIFIC_AND_TECHNICAL_SERVICES = 'PROFESSIONAL_SCIENTIFIC_AND_TECHNICAL_SERVICES',
    PUBLIC_ADMINISTRATION = 'PUBLIC_ADMINISTRATION',
    REAL_ESTATE_AND_RENTAL_AND_LEASING = 'REAL_ESTATE_AND_RENTAL_AND_LEASING',
    RETAIL_TRADE = 'RETAIL_TRADE',
    TRANSPORTATION_AND_WAREHOUSING = 'TRANSPORTATION_AND_WAREHOUSING',
    UTILITIES = 'UTILITIES',
    WHOLESALE_TRADE = 'WHOLESALE_TRADE',
}

export const READABLE_INDUSTRY_TYPE_MAP: Record<string, IndustryType> = {
    'Accommodation and Food Services':
        IndustryType.ACCOMMODATION_AND_FOOD_SERVICES,
    'Administrative and Support and Waste Management and Remediation Services':
        IndustryType.ADMINISTRATIVE_AND_SUPPORT_AND_WASTE_MANAGEMENT_AND_REMEDIATION_SERVICES,
    'Agriculture, Forestry, Fishing, and Hunting':
        IndustryType.AGRICULTURE_FORESTRY_FISHING_AND_HUNTING,
    'Arts, Entertainment, and Recreation':
        IndustryType.ARTS_ENTERTAINMENT_AND_RECREATION,
    Construction: IndustryType.CONSTRUCTION,
    'Educational Services': IndustryType.EDUCATIONAL_SERVICES,
    'Finance and Insurance': IndustryType.FINANCE_AND_INSURANCE,
    'Healthcare and Social Assistance':
        IndustryType.HEALTH_CARE_AND_SOCIAL_ASSISTANCE,
    Information: IndustryType.INFORMATION,
    'Management of Companies and Enterprises':
        IndustryType.MANAGEMENT_OF_COMPANIES_AND_ENTERPRISES,
    Manufacturing: IndustryType.MANUFACTURING,
    'Mining, Quarrying, and Oil and Gas Extraction':
        IndustryType.MINING_QUARRYING_AND_OIL_AND_GAS_EXTRACTION,
    'Other Services': IndustryType.OTHER_SERVICES,
    'Professional Scientific and Technical Services':
        IndustryType.PROFESSIONAL_SCIENTIFIC_AND_TECHNICAL_SERVICES,
    'Public Administration': IndustryType.PUBLIC_ADMINISTRATION,
    'Real Estate and Rental and Leasing':
        IndustryType.REAL_ESTATE_AND_RENTAL_AND_LEASING,
    'Retail Trade': IndustryType.RETAIL_TRADE,
    'Transportation and Warehousing':
        IndustryType.TRANSPORTATION_AND_WAREHOUSING,
    Utilities: IndustryType.UTILITIES,
    'Wholesale Trade': IndustryType.WHOLESALE_TRADE,
};

/**
 * Returns display label for employment type (e.g. FULL_TIME -> "Employed").
 * Falls back to formatted value (e.g. CONTRACTOR -> "Contractor") when not in READABLE_EMPLOYMENT_TYPE_MAP.
 */
export const getEmploymentTypeDisplayLabel = (
    employmentType: string,
): string => {
    const entry = Object.entries(READABLE_EMPLOYMENT_TYPE_MAP).find(
        ([, value]) => value === (employmentType as EmploymentType),
    );
    if (entry != null) {
        return entry[0];
    }
    return employmentType
        .split('_')
        .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
        .join(' ');
};

/**
 * Returns display label for occupation industry (e.g. FINANCE_AND_INSURANCE -> "Finance and Insurance").
 */
export const getIndustryTypeDisplayLabel = (industryType: string): string => {
    const entry = Object.entries(READABLE_INDUSTRY_TYPE_MAP).find(
        ([, value]) => value === (industryType as IndustryType),
    );
    return entry?.[0] ?? industryType;
};
